// src/utils/fulfillmentStatus.ts
// Single source of truth for how a Square fulfillment state is presented to
// customers across the order list, order detail, and tracking pages.
//
// The customer-facing status is driven by the *fulfillment* state (what the
// buyer actually tracks), not the raw Square order state. We deliberately
// avoid the word "Processing" for RESERVED — to a shopper that reads like a
// payment problem, when in fact the payment has already cleared and the item
// is being made to order (print-on-demand).

/**
 * Has this order actually shipped?
 *
 * Read this from the FULFILLMENT, never from `order.state`. The
 * `printful-webhook` sets the SHIPMENT fulfillment to COMPLETED and
 * deliberately leaves the *order* state OPEN (the refund/cancel paths branch on
 * OPEN/RESERVED), so `order.state === "COMPLETED"` is only ever true for an
 * order somebody marked shipped by hand in the Square dashboard. Anything
 * customer-facing that means "shipped" — review eligibility, returns — must
 * key off this.
 */
export const hasShipped = (order?: {
  fulfillments?: { state?: string | null }[] | null;
  state?: string | null;
}): boolean =>
  !!order?.fulfillments?.some((f) => f?.state === "COMPLETED") ||
  order?.state === "COMPLETED";

export const fulfillmentLabel = (state?: string): string => {
  switch (state) {
    case "PROPOSED":
      return "Order Received";
    case "RESERVED":
      return "In Production";
    case "PREPARED":
      return "Ready to Ship";
    case "COMPLETED":
      return "Shipped";
    case "CANCELED":
      return "Canceled";
    case "FAILED":
      return "Issue";
    // Fallback for an order that has no fulfillment yet (raw Square order state).
    case "OPEN":
      return "In Progress";
    default:
      return state ?? "Pending";
  }
};

// Short, reassuring plain-English explanation of what each stage means.
// Shown in a hover tooltip on the status badge.
export const fulfillmentDescription = (state?: string): string => {
  switch (state) {
    case "PROPOSED":
    case "OPEN":
      return "Payment confirmed. We're getting your order ready for production.";
    case "RESERVED":
      return "Your payment cleared — your item is now being made to order.";
    case "PREPARED":
      return "Made and packed. It's ready to hand off to the carrier.";
    case "COMPLETED":
      return "Your order has shipped. Use Track for the latest delivery updates.";
    case "CANCELED":
      return "This order was canceled.";
    case "FAILED":
      return "Something went wrong with this order — we are working on a fix, please contact support with any questions.";
    default:
      return "We're processing your order.";
  }
};

export const fulfillmentBadgeClass = (state?: string): string => {
  switch (state) {
    case "PROPOSED":
    case "RESERVED":
      return "order-state-badge order-state-badge--warm";
    case "PREPARED":
      return "order-state-badge order-state-badge--blue";
    case "COMPLETED":
      return "order-state-badge order-state-badge--green";
    case "CANCELED":
    case "FAILED":
      return "order-state-badge order-state-badge--red";
    default:
      return "order-state-badge order-state-badge--warm";
  }
};
